import type { LocaleMessages } from './en'

export const fr: LocaleMessages = {
	setupWizard: {
		// Steps
		STEP_1_TITLE: 'Commencer',
		STEP_1_SUBTITLE: 'Configurez les bases de votre création',
		STEP_2_TITLE: 'Choisissez le logo de votre entreprise',
		STEP_2_SUBTITLE:
			"Choisissez un logo pour qu'il apparaisse dans l'en-tête et/ou le pied de page",
		STEP_3_TITLE: 'Transformez les produits en échantillons virtuels',
		STEP_3_SUBTITLE:
			'Choisissez un logo à appliquer aux produits que vous ajoutez à votre design',

		// Form fields
		ADD_NAME: 'Donnez un nom à votre création.',
		SELECT_CURRENCY: 'Choisissez une devise',
		USE_AUTO_LOGO: 'Ajouter automatiquement le logo à votre design',
		SELECT_LOGO: 'Sélectionner le logo',
		UNTITLED: 'Sans titre',
		SELECT_YOUR_DESIGN_TYPE: 'Orientation',

		// Orientation
		PORTRAIT: 'Portrait',
		LANDSCAPE: 'Paysage',

		// Buttons
		BACK: 'Retour',
		CONTINUE: 'Continuer',
		SKIP: 'Passer',
		STEP_OF: 'Étape {current} sur {total}',

		// Messages
		NAME_IS_REQUIRED:
			'Le champ nom est obligatoire et doit contenir moins de 64 caractères',
		UNABLE_TO_SAVE_DESIGN_SETTINGS:
			"Impossible d'enregistrer les paramètres du design, veuillez réessayer",
	},

	pagination: {
		ADD_PAGE: 'Ajouter une page',
		SINGLE_PAGE: 'Page simple',
		SPREAD_PAGE: 'Double page',
		DELETE_PAGE: (count?: number) =>
			(count ?? 0) > 1 ? `Supprimer ${count} pages` : 'Supprimer la page',
		DUPLICATE_PAGE: 'Dupliquer la page',
		DELETE_CONFIRMATION: (count?: number) =>
			(count ?? 0) > 1
				? `Êtes-vous sûr de vouloir supprimer les ${count} pages sélectionnées?`
				: 'Êtes-vous sûr de vouloir supprimer cette page?',
		YOU_CANNOT_SELECT_MORE_THAN: (count?: number) =>
			`Vous ne pouvez pas sélectionner plus de ${count} pages à la fois`,
	},

	startScreen: {
		// Header
		TITLE: 'Commencer',
		SUBTITLE:
			'Commencez avec un modèle vierge, utilisez un gabarit ou explorez vos créations.',
		NEW_DESIGN: 'Créer une nouvelle création',

		// Sections
		MY_DESIGNS: 'Mes créations',
		TEMPLATES: 'Modèles',
		TEMPLATES_SS: 'Gabarit S&S',
		BROWSE_DESIGNS: 'Parcourir mes créations',
		CONTINUE_WORKING: 'Continuez à travailler sur vos designs',

		// Design actions
		CREATE: 'Créer',
		EDIT: 'Modifier',
		USE_TEMPLATE: 'Utiliser le modèle',

		// Design card
		CREATE_NEW_DESIGN: 'Créer une nouvelle création',

		// Options menu
		VIEW_DESIGN: 'Voir le design',
		DUPLICATE_DESIGN: 'Dupliquer le design',
		DELETE_DESIGN: 'Supprimer le design',

		// Delete confirmation
		DESIGN: 'Design',
		DELETE_CONFIRMATION: 'Êtes-vous sûr de vouloir supprimer le design',
		DELETE_YES: 'Oui, supprimer le design',
		CANCEL: 'Annuler',
		DELETE_SUCCESS: 'Le design a été supprimé avec succès',

		// Loading
		LOADING_MORE: 'Chargement en cours',
		NO_MORE_RESULTS: 'Plus de résultats',
	},

	setupWizardCompleted: {
		TITLE: 'Tout est prêt ! Commencez avec votre création maintenant.',
		DESCRIPTION:
			'Vous pouvez modifier ces paramètres à tout moment en cliquant sur',
		DESIGN_SETTINGS: 'Paramètres de création',
		CONTINUE_TO_DESIGN: 'Continuer vers votre design',
		CONTINUE_TO_ADD_PRODUCTS: 'Ajouter des produits',
		PROCESSING: 'Traitement en cours...',
	},

	toolbar: {
		// Publish
		SHARE: 'Partager',
		PUBLISH_DESIGN: 'Publier',
		PUBLISH_CHANGES: 'Publier les modifications',
		DESIGN_PRIVATE: 'Ce design est privé.',
		SHARE_YOUR_DESIGN_INSTANTLY: 'Partagez votre design instantanément!',
		SHARE_YOUR_DESIGN_INSTANTLY_DESCRIPTION:
			'Publiez votre design pour obtenir un lien partageable pour les e-mails, les sites web, les réseaux sociaux et plus encore!',

		// Design Settings
		DESIGN_SETTINGS: 'Paramètres de création',
		DRAFT_SAVED: 'Brouillon enregistré',
		SAVING: 'Enregistrement...',
		MANAGE_YOUR_DESIGN_SETTINGS: 'Gérer les paramètres de votre design',
		UPDATE_YOUR_DESIGN_NAME_AND_LOGO:
			"Mettez à jour le nom et le logo de votre design, gérez vos paramètres d'échantillons virtuels et plus encore.",

		// Manage Page
		MANAGE_PAGE: 'Gérer la page',
		LEFT_PAGE: 'Page gauche',
		RIGHT_PAGE: 'Page droite',
		REORDER_PAGES: 'Réorganiser les pages',
		IS_TEMPLATE: 'Ce design est un modèle',
		TEMPLATE_TEXT:
			'Partagez ce design comme modèle pour permettre aux autres de le dupliquer et de le personnaliser.',
		WATCH_TUTORIAL: 'Regarder le tutoriel',
		CHANGE_THE_PAGE_LAYOUT: 'Modifier la mise en page',
		CHANGE_THE_PAGE_LAYOUT_DESCRIPTION:
			'Page de couverture? Deux produits et une bannière? Un seul produit vedette? Choisissez la mise en page parfaite ici!',

		// Errors
		INVALID_PROJECT:
			'Détails du projet manquants. Veuillez réessayer ou contacter le support.',
	},

	pageSidebar: {
		MANAGE_PAGE: 'Gérer la page',
		SELECT_LAYOUT_TITLE: 'Sélectionner la mise en page',
		PAGE_SETTINGS_TITLE: 'Informations produit sur la page',
		MANAGE_PAGE_CONTENT: 'Gérer le contenu de la page',
		SIDEBAR_SETTINGS_TITLE: 'Informations produit dans la barre latérale',
		CHANGE_LAYOUT_BTN: 'Changer la mise en page',
		LAYOUT_NAMES: {
			BASIC_FOUR: '4 Produits de Base',
		},
	},

	designSettings: {
		DESIGN_SETTINGS: 'Paramètres de création',
		GENERAL: 'Général',
		TEMPLATE: 'Modèle',
		VIRTUALS: 'Échantillons virtuels',
		DATA: 'Données',
		CREATE_DESIGN: 'Créer un design',
		SAVE_SETTINGS: 'Sauvegarder vos paramètres',
		NAME_IS_REQUIRED:
			'Le champ nom est obligatoire et doit contenir moins de 64 caractères',
		UNABLE_TO_SAVE_DESIGN_SETTINGS:
			"Impossible d'enregistrer les paramètres du design, veuillez réessayer",

		// General Tab
		ADD_NAME: 'Ajoutez un nom à votre création',
		SELECT_CURRENCY: 'Sélectionnez la devise pour ce design',
		USE_AUTO_LOGO: 'Ajouter automatiquement le logo à votre design',
		SELECT_LOGO: 'Sélectionnez votre logo',
		UNTITLED: 'Sans titre',
		SELECT_YOUR_DESIGN_TYPE: 'Sélectionnez le type de création',
		PORTRAIT: 'Portrait',
		LANDSCAPE: 'Paysage',

		// Data Tab
		MARK_UP_DOWN_PRICES: 'Marquer/réduire vos prix',
		DISCOUNT: 'Remise',
		MARK_UP: 'Marge',
	},

	userMenu: {
		NEW: 'Créer une nouvelle création',
		MY_DESIGNS: 'Mes créations',
		DASHBOARD: 'Tableau de Bord',
		SIGN_OUT: 'Se Déconnecter',
	},

	layoutPicker: {
		SELECT_LAYOUT: 'Sélectionnez une mise en page',
		LAYOUT_CATEGORIES: 'Catégories de mise en page',
		ALL_LAYOUTS: 'Toutes les mises en page',
		COVER_PAGES: 'Pages de couverture',
		BASIC_LAYOUTS: 'Mises en page de base',
		GRAPHIC_LAYOUTS: 'Mises en page graphiques',
		VIRTUALS_LAYOUTS: 'Mises en page virtuelles',
		TEXT_LAYOUTS: 'Mises en page texte',

		// Help section
		NEED_NEW_TEMPLATE: "Besoin d'un nouveau modèle?",
		TEMPLATE_LIBRARY_GROWING:
			"La bibliothèque de modèles s'agrandit rapidement! Dites-nous ce que vous recherchez.",
		SEND_SUGGESTION: 'Envoyer une suggestion',

		// Empty state
		MORE_TEMPLATES_COMING: 'Plus de modèles à venir bientôt',
		ALWAYS_ADDING_NEW:
			'Nous ajoutons toujours de nouveaux modèles. Revenez bientôt.',

		// Layout actions
		USE_LAYOUT: 'Utiliser la mise en page',
		SELECTED: 'Sélectionné',
		NOT_AVAILABLE: 'Non disponible',
		COMING_SOON: 'Bientôt disponible',
		LAYOUT_NOT_AVAILABLE_TEXT:
			"Vous avez ajouté plus de produits au canevas que cette mise en page ne peut en supporter. Pour l'utiliser, supprimez certains produits.",
	},

	product: {
		ALL_COLORS: 'Toutes les Couleurs',
	},

	publishModal: {
		PUBLISH_TEMPLATE: 'Publier le modèle',
		PUBLISH_TEMPLATE_CHANGES: 'Publier les modifications du modèle',
		PUBLISH_TEMPLATE_TITLE: 'Êtes-vous prêt à publier votre modèle?',
		PUBLISH_TEMPLATE_TEXT:
			"Une fois que vous avez publié votre design comme modèle, vous pouvez le partager avec d'autres personnes afin qu'elles puissent facilement le personnaliser.",
		PUBLISH_CHANGES: 'Publier les modifications',
		PUBLISH_CHANGES_TITLE:
			'Êtes-vous sûr de vouloir publier ces modifications à votre modèle?',
		PUBLISH_CHANGES_TEXT:
			'Ces modifications seront rendues publiques à ceux qui ont accès à votre design dans la visionneuse. Cela ne peut pas être annulé.',
		PUBLISH_DESIGN: 'Prêt à publier et partager votre création ?',
		YES_PUBLISH: 'Oui, publier',
		CANCEL: 'Annuler',
	},

	shareModal: {
		SHARE_DESIGN: 'Partager votre création',
		SHARE_TEMPLATE: 'Partager votre modèle',
		SEND_LINK_TITLE:
			'Partagez votre création par courriel, réseaux sociaux, votre site web et plus encore.',
		SEND_LINK_TITLE_TEMPLATE: 'Êtes-vous prêt à partager votre modèle?',
		SEND_LINK_DESCRIPTION: 'Il suffit de copier et coller le lien ci-dessous.',
		COPY_LINK: 'Copier le lien',
		COPY_LINK_TEMPLATE: 'Copier le lien vers votre modèle',
		COPY_LINK_DESCRIPTION:
			'Toute personne avec ce lien peut consulter votre création.',
		COPY_LINK_DESCRIPTION_TEMPLATE:
			'Cela signifie que toute personne ayant votre lien partageable peut voir votre design.',
		MAKE_PRIVATE: 'Le rendre privé?',
		MAKE_PRIVATE_DESCRIPTION:
			'Si vous rendez votre design privé, les personnes ayant le lien ne pourront pas le voir.',
		OPEN_LINK: 'Ouvrir le lien',
	},

	downloadBar: {
		DOWNLOAD: 'Télécharger PDF',
	},

	productDataControl: {
		MANAGE_PRODUCT: 'Gérer le produit',
		PRODUCT_DATA: 'Données du produit',
		PAGE: 'Page',
		CANVAS: 'Page',
		SIDEBAR: 'Barre latérale',
		DATA: 'Données',
		TEMPLATE: 'Modèle',
		UPDATE_ALL: 'Tout Mettre à Jour',
		COLORS: 'Couleurs',
		COLOR: 'Couleur',
		SIZES: 'Tailles',
		NO_IMAGES: 'Aucune image',
		NO_IMAGES_SELECTED: 'Aucune image sélectionnée',
		SEE_ALL: 'Voir tout',
		NO_COLORS_SELECTED: 'Aucune couleur sélectionnée',
		NO_SIZES_SELECTED: 'Aucune taille sélectionnée',
		UPLOADS: 'Téléchargements',
		DEFAULT: 'Défaut',
		BASIC: 'Basique',
		QUANTITY: 'Quantité',
		PRICE: 'Prix',
		ACTIONS: 'Actions',
		PRICING_CODE: 'Code de prix',
		PRICING_TABLE: 'Tableau de prix',
		SINGLE_PRICE: 'Prix unique',
		MOQ_IS_AUTOMATICALLY_POPULATED:
			'Le MOQ est automatiquement rempli en fonction de vos données de prix.',
		ALLOW_REPLACING_THIS_PRODUCT: 'Autoriser le remplacement de ce produit?',
		ALLOW_EDITING_PRODUCT_INFORMATION:
			'Autoriser la modification des informations produit?',
		YES_THIS_PRODUCT_CAN_BE_REPLACED: 'Oui, ce produit peut être remplacé.',
		YES_THE_PRODUCT_INFORMATION_CAN_BE_EDITED:
			'Oui, les informations du produit peuvent être modifiées.',
		EDITING_DISABLED_FOR_THIS_PRODUCT:
			'La modification a été désactivée pour ce produit par le créateur du modèle.',
		PLEASE_PROVIDE_A_VALID_PRICE: 'Veuillez fournir un prix valide',
		OTHER_FIELDS: 'Autres champs',
		HERE_YOU_CAN_EDIT_THE_PRODUCT_DATA:
			'Ici vous pouvez modifier les données du produit pour ce champ',
		NO_MORE_OPTIONS_TO_SELECT: "Plus d'options à sélectionner",
		VIRTUALS: 'Virtuels',
		YOUR_LOGO: 'Votre logo',
		PICK_IMPRINT_AREA: "Sélectionner la zone d'impression",
		CREATE: 'Créer',
		YOUR_VIRTUALS: 'Vos virtuels',
		NO_VIRTUALS_ENABLED: 'Aucun virtuel activé',
		NO_VIRTUALS_ANYMORE: 'Plus de virtuels',
		NO_VIRTUALS_CREATED: 'Aucun virtuel créé',
		NO_IMPRINT_AREAS: "Aucune zone d'impression",
		SELECTED_IMAGE: 'Image sélectionnée',
		UNSELECTED_IMAGE: 'Image non sélectionnée',
		VS: 'VS',
		ADD_LOGO: 'Ajouter un logo',
		ALL_IMAGES: 'Toutes les images',
		MY_UPLOADS: 'Mes téléchargements',
		MY_VIRTUALS: 'Mes virtuels',
		VIRTUAL_SAMPLE_ENABLED: 'Échantillon virtuel activé',
		PRODUCT_IMAGES: 'Images du produit',
		ALL_COLORS: 'Toutes les couleurs',
		CREATE_VIRTUAL_SAMPLES: 'Créer des Échantillons Virtuels',
		UNNAMED: 'Sans nom',
		CALCULATED_PRICING: 'Prix calculé',
		DISCOUNT: 'Remise',
		MARK_UP: 'Marge',
		ENABLED: 'Activé',
		DISABLED: 'Désactivé',
		ADD_ALL: 'Tout ajouter',
		USE_LIST_PRICING: 'Utiliser la liste de prix',
		ADD_MY_OWN_PRICING: 'Ajouter mon propre prix',
		NO_PRICING: 'Aucun prix',
		YOU_HAVE_SELECTED_TO_REMOVE_ANY_PRICING:
			'Vous avez choisi de supprimer tout prix de ce produit.',
		DO_YOU_WANT_TO_ADD_PRICE_PLACEHOLDER:
			'Voulez-vous ajouter un espace réservé pour le prix?',
		NO_LEAVE_IT_BLANK: 'Non, le laisser vide',
		YES_ADD_CALL_FOR_PRICING: 'Oui, ajouter "Appelez pour le prix"',
		PRICE_CODE: 'Code de prix',
		INCLUDE_PRICE_CODE: 'Inclure le code de prix',
		ADD_A_MARK_UP_DOWN: 'Ajouter une marge/remise',
		APPLY: 'Appliquer',
		REMOVE: 'Supprimer',
		THIS_PRODUCT_USES_THE_GLOBAL_MARKUP: 'Ce produit utilise la marge globale',
		ENABLE_A_PRODUCT_SPECIFIC_MARKUP: 'Activer une marge spécifique au produit',
		MARK_UP_DOWN_PRICES: 'Marge/remise des prix',
		EDIT: 'Modifier',
		SHOW_IN_SMART_SIDEBAR: 'Afficher dans la barre latérale intelligente',
		APPLY_TO_ALL_ON_PAGE: 'Appliquer à tous sur la page',
		APPLY_TO_PAGES_SAME_LAYOUT:
			'Appliquer aux pages utilisant la même mise en page',
		UPDATE_ALL_SIDEBARS: 'Mettre à jour toutes les barres latérales',
		NO_DATA_CAN_BE_EDITED: 'Aucune donnée ne peut être modifiée',
		DATA_CONTROLS: {
			PRODUCT_ID: 'ID du produit',
			PRODUCT_IMAGE: 'Photos du produit',
			PRODUCT_NAME: 'Nom du produit',
			DESCRIPTION: 'Description',
			MATERIALS: 'Matériaux',
			COLORS: 'Couleurs',
			SIZES: 'Tailles',
			PRICING: 'Tarification',
			MOQ: 'MOQ',
			WEBSITE: "Appel à l'action",
			PRODUCT_IMAGE_TWO: 'Deuxième image du produit',
			PRICE_CODE: 'Code de prix',
		},
	},

	common: {
		// Actions
		SAVE: 'Enregistrer',
		CANCEL: 'Annuler',
		DELETE: 'Supprimer',
		EDIT: 'Modifier',
		ADD: 'Ajouter',
		CLOSE: 'Fermer',
		CONFIRM: 'Confirmer',
		BACK: 'Retour',
		CONTINUE: 'Continuer',
		SKIP: 'Passer',

		// States
		LOADING: 'Chargement...',
		WELCOME: 'Bienvenue sur Smart Layouts',
		ERROR: 'Erreur',
		SUCCESS: 'Succès',
		WARNING: 'Avertissement',

		// Validation
		VALUE_MUST_BE_MAX_64_CHARS:
			'La valeur doit contenir au maximum 64 caractères',
		FIELD_REQUIRED_VALID_INPUT:
			'Ce champ est obligatoire, veuillez entrer une valeur valide',

		// Error Messages
		UNABLE_TO_LOAD_APP_DETAILS:
			"Impossible de charger les détails de l'application. Veuillez réessayer ou contacter le support",
		PLEASE_TRY_AGAIN_OR_CONTACT_SUPPORT:
			'Veuillez réessayer ou contacter le support.',
		TRY_REFRESHING_OR_CONTACT_SUPPORT:
			'Essayez de rafraîchir ou contactez le support.',
		SOMETHING_WENT_WRONG: 'Quelque chose a mal tourné',
		FORBIDDEN_PROJECT_NOT_ASSOCIATED:
			"Projet interdit non associé à l'entreprise",
		PROJECT_LOCKED_RETRY_LATER:
			'Actuellement, le projet est verrouillé, veuillez réessayer plus tard.',
		PENDING_UPDATE_DETECTED:
			'Mise à jour en attente détectée, application en cours. Veuillez patienter...',
	},

	errors: {
		// Auth Errors
		UNABLE_TO_REFRESH_TOKEN: 'Impossible de rafraîchir le jeton',
		UNABLE_TO_VALIDATE_EMAIL: "Impossible de valider l'e-mail",
		UNABLE_TO_LOGIN: 'Impossible de se connecter (vérifiez vos identifiants)',
		UNABLE_TO_LOGOUT: 'Impossible de se déconnecter',
		UNABLE_TO_REQUEST_PASSWORD_RESET:
			'Impossible de demander une réinitialisation de mot de passe',
		UNABLE_TO_RESET_PASSWORD: 'Impossible de réinitialiser le mot de passe',
		UNABLE_TO_VERIFY_RESET_PASSWORD:
			'Impossible de vérifier la demande de réinitialisation de mot de passe',
		UNABLE_TO_SIGN_UP: "Impossible de s'inscrire",
		UNABLE_TO_RESEND_SIGNUP_VERIFICATION:
			"Impossible de renvoyer la vérification d'inscription",
		UNABLE_TO_VERIFY_SIGNUP: "Impossible de vérifier l'inscription",

		// Product Errors
		UNABLE_TO_LOAD_PRODUCTS: 'Impossible de charger les produits',
		UNABLE_TO_LOAD_PRODUCT_DETAILS:
			'Impossible de charger les détails du produit',
		UNABLE_TO_LOAD_PRODUCT_ATTRIBUTES:
			'Impossible de charger les attributs du produit',

		// Asset Errors
		UNABLE_TO_LOAD_ASSET: 'Impossible de charger la ressource',
		UNABLE_TO_PREPARE_UPLOAD: 'Impossible de préparer le téléchargement',
		UNABLE_TO_LOAD_ASSETS: 'Impossible de charger les ressources',
		UNABLE_TO_UPLOAD_ASSET: 'Impossible de télécharger la ressource',
		UNABLE_TO_LOAD_UNWRAPPED_S3_URL: "Impossible de charger l'URL S3 déballée",
		UNABLE_TO_LOAD_IMPRINT_AREAS: "Impossible de charger les zones d'impression",
		UNABLE_TO_LOAD_ASSET_SETTINGS:
			'Impossible de charger les paramètres de la ressource',
		UNABLE_TO_LOAD_ASSET_VARIATIONS:
			'Impossible de charger les variations de la ressource',
		UNABLE_TO_CREATE_ASSET_VARIATION:
			'Impossible de créer la variation de la ressource',
		UNABLE_TO_LOAD_ASSET_VARIATION:
			'Impossible de charger la variation de la ressource',

		// Virtual Sample Errors
		UNABLE_TO_LOAD_VIRTUAL_SAMPLE: "Impossible de charger l'échantillon virtuel",
		UNABLE_TO_LOAD_VIRTUAL_SAMPLES:
			'Impossible de charger les échantillons virtuels',
		UNABLE_TO_CREATE_VIRTUAL_SAMPLE: "Impossible de créer l'échantillon virtuel",
		UNABLE_TO_EDIT_VIRTUAL_SAMPLE: "Impossible de modifier l'échantillon virtuel",
		UNABLE_TO_CREATE_VIRTUAL_SAMPLES:
			'Impossible de créer les échantillons virtuels',
	},

	productFilters: {
		TYPE_AND_SEARCH: 'Taper et rechercher',
		NEW_ARRIVALS: 'Nouveautés',
		PRICE: 'Prix',
		CATEGORY: 'Catégorie',
		BRAND: 'Marque',
		TYPE: 'Type',
		DEPARTMENT: 'Département',
		LICENSE: 'Licence',
		TREND: 'Tendance',
		SEASON: 'Saison',
		ADD_FILTER: 'Ajouter un filtre',
	},

	templateSettings: {
		WATCH_TUTORIAL: 'Regarder le tutoriel',
		LET_OTHERS_DUPLICATE_AND_EDIT_YOUR_DESIGN:
			'Transformez votre design en un modèle modifiable.',
		YOU_CAN_SHARE_YOUR_DESIGN:
			'Le partage comme modèle permet aux autres de dupliquer et de personnaliser votre design comme le leur.',
		DO_YOU_WANT_TO_MAKE_THIS_DESIGN_A_TEMPLATE:
			'Voulez-vous faire de ce design un modèle partageable?',
		A_TEMPLATE_ALLOWS_OTHERS_TO_DUPLICATE_YOUR_DESIGN:
			"Un modèle permet aux autres de dupliquer votre design et de l'utiliser pour eux-mêmes.",
	},

	shareTemplateModal: {
		SEND_LINK_DESCRIPTION_TEMPLATE:
			'Le partage comme modèle permet aux autres de dupliquer et de personnaliser votre design comme le leur.',
		ADD_TEMPLATE_PORTAL_TITLE: 'Ajouter le modèle à la bibliothèque',
		ADD_TEMPLATE_PORTAL_DESCRIPTION:
			"Lorsqu'il est activé, ce modèle sera disponible dans votre bibliothèque de modèles ZOOMstudio pour les distributeurs et votre équipe.",
		SET_TEMPLATES_PERMISSIONS_TITLE: 'Définir les permissions du modèle',
		SET_TEMPLATES_PERMISSIONS_DESCRIPTION:
			'Choisissez qui peut voir et utiliser ce modèle.',
		ONLY_YOU: 'Vous seulement',
		YOUR_TEAM: 'Votre équipe',
		PUBLIC: 'Public',
	},

	virtualsTab: {
		WATCH_VIDEO: 'Regarder la vidéo',
		TITLE: 'Créer des Échantillons Virtuels',
		SUBTITLE:
			"Téléchargez un logo pour l'appliquer automatiquement aux produits de ce design.",
		SELECT_YOUR_ARTWORK: 'Sélectionner le logo',
		USE_VIRTUALS_BY_DEFAULT: 'Appliquer les logos aux produits',
	},

	virtualsProcessor: {
		FETCHING_PAGES: 'Collecte des pages',
		FETCHING_IMPRINT_AREAS: "Recherche des zones d'impression",
		QUEUE: "Mise en file d'attente pour traitement",
		PROCESSING_VIRTUAL: 'Traitement virtuel',
		IN_PROGRESS: (count: number, total: number) =>
			`Traitement de ${count} sur ${total} virtuels`,
	},

	assetPicker: {
		FILE_TOO_LARGE: 'Désolé, votre fichier est trop volumineux',
		FILE_TOO_SMALL: 'Désolé, votre fichier est trop petit',
		TOO_MANY_FILES: 'Désolé, vous avez essayé de télécharger trop de fichiers',
		INVALID_FILE_TYPE: 'Désolé, nous ne prenons en charge que JPG/JPEG/PNG',
		SOMETHING_WENT_WRONG:
			"Quelque chose s'est mal passé lors de la sélection de la ressource",
		UPDATE_ERROR:
			"Une erreur s'est produite lors de la mise à jour. Veuillez actualiser la page.",
	},

	virtualSampleEditor: {
		REPOSITION_LOGO: 'Repositionner le logo',
		SAVE_CHANGES: 'Enregistrer les modifications',
		DISCLAIMER_TEXT:
			"Avertissement : Les échantillons virtuels sont uniquement à des fins illustratives et ne garantissent pas l'apparence du produit imprimé final. Les zones d'impression du produit peuvent avoir des restrictions sur le placement, la couleur et la taille du logo.",
	},

	logoEditor: {
		EDIT_LOGO: 'Modifier le logo',
	},

	productImageField: {
		IMAGES_AND_VIRTUAL_SAMPLES: 'Images et Échantillons Virtuels',
		IMAGE_SINGULAR: 'Image',
		IMAGES_PLURAL: 'Images',
	},
}
